import React, { useEffect, useRef, useState } from 'react';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import { Ruler, Target, Info, Crosshair, ArrowCounterClockwise, Camera } from '@phosphor-icons/react';

export default function Tools() {
    const [tab, setTab] = useState('level');
    return (
        <div className="min-h-screen bg-[#09090B] text-white pb-24">
            <div className="bg-white text-[#09090B]">
                <AppHeader title="Tools" subtitle="Calibrated on-device instruments" />
            </div>
            <div className="border-b border-white/10 bg-[#09090B]">
                <div className="max-w-3xl mx-auto flex">
                    <button
                        onClick={() => setTab('level')}
                        data-testid="tab-level"
                        className={`flex-1 py-4 font-mono text-xs uppercase tracking-[0.2em] border-r border-white/10 inline-flex items-center justify-center gap-2 ${tab === 'level' ? 'bg-[#1FA5CF] text-white' : 'text-white/70 hover:text-white'}`}
                    >
                        <Target size={16} /> Spirit Level
                    </button>
                    <button
                        onClick={() => setTab('measure')}
                        data-testid="tab-measure"
                        className={`flex-1 py-4 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 ${tab === 'measure' ? 'bg-[#1FA5CF] text-white' : 'text-white/70 hover:text-white'}`}
                    >
                        <Ruler size={16} /> Measure
                    </button>
                </div>
            </div>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                {tab === 'level' ? <SpiritLevel /> : <MeasureTool />}
            </main>
            <div className="bg-white"><BottomNav /></div>
        </div>
    );
}

function SpiritLevel() {
    const [beta, setBeta] = useState(0);
    const [gamma, setGamma] = useState(0);
    const [granted, setGranted] = useState(false);
    const [unsupported, setUnsupported] = useState(false);
    const [mode, setMode] = useState('surface'); // 'surface' | 'wall'
    const [calib, setCalib] = useState({ beta: 0, gamma: 0 });
    const handlerRef = useRef(null);
    const smoothRef = useRef({ beta: 0, gamma: 0, init: false });

    const requestPermission = async () => {
        try {
            if (typeof window.DeviceOrientationEvent?.requestPermission === 'function') {
                const perm = await window.DeviceOrientationEvent.requestPermission();
                if (perm !== 'granted') { setUnsupported(true); return; }
            }
            // Low-pass EMA filter — alpha controls responsiveness vs stability.
            // Lower alpha = smoother but laggier. 0.12 gives a calm, stable feel.
            const ALPHA = 0.12;
            const h = (e) => {
                const rawB = e.beta ?? 0;
                const rawG = e.gamma ?? 0;
                const s = smoothRef.current;
                if (!s.init) {
                    s.beta = rawB; s.gamma = rawG; s.init = true;
                } else {
                    s.beta = s.beta * (1 - ALPHA) + rawB * ALPHA;
                    s.gamma = s.gamma * (1 - ALPHA) + rawG * ALPHA;
                }
                setBeta(s.beta);
                setGamma(s.gamma);
            };
            handlerRef.current = h;
            window.addEventListener('deviceorientation', h);
            setGranted(true);
        } catch {
            setUnsupported(true);
        }
    };

    useEffect(() => () => { if (handlerRef.current) window.removeEventListener('deviceorientation', handlerRef.current); }, []);

    // Apply calibration offset
    const b = beta - calib.beta;
    const g = gamma - calib.gamma;

    // Dead-zone: snap to zero when extremely close, removes jitter near level
    const snap = (v) => (Math.abs(v) < 0.2 ? 0 : v);
    const bs = snap(b);
    const gs = snap(g);

    const LEVEL_TOLERANCE = 1.5; // was 1.0 — more forgiving, matches Apple
    const isLevel = Math.abs(bs) < LEVEL_TOLERANCE && Math.abs(gs) < LEVEL_TOLERANCE;
    const vialLevel = (axisVal) => Math.abs(axisVal) < LEVEL_TOLERANCE;
    const bigAngle = mode === 'wall' ? Math.abs(gs) : Math.max(Math.abs(bs), Math.abs(gs));

    // Reduced travel: at 30° tilt the bubble hits the edge (was 45°/150%)
    const clampedX = Math.max(-30, Math.min(30, gs));
    const clampedY = Math.max(-30, Math.min(30, bs));

    const zeroNow = () => setCalib({ beta, gamma });
    const resetCalib = () => setCalib({ beta: 0, gamma: 0 });

    return (
        <div>
            {/* Mode toggle */}
            <div className="flex items-center gap-0 border border-white/15 mb-4" data-testid="level-mode-toggle">
                <button
                    onClick={() => setMode('surface')}
                    data-testid="mode-surface"
                    className={`flex-1 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border-r border-white/15 ${mode === 'surface' ? 'bg-[#1FA5CF] text-white' : 'text-white/70'}`}
                >Surface</button>
                <button
                    onClick={() => setMode('wall')}
                    data-testid="mode-wall"
                    className={`flex-1 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] ${mode === 'wall' ? 'bg-[#1FA5CF] text-white' : 'text-white/70'}`}
                >Wall · Vial</button>
            </div>

            {/* Big angle readout */}
            <div
                className={`border transition-colors duration-200 mb-4 ${isLevel && granted ? 'border-[#00875A] bg-[#00875A]/15' : 'border-white/15 bg-[#0A0A0C]'}`}
                data-testid="level-readout"
            >
                <div className="py-8 text-center">
                    <div className="label-tiny text-white/60">{mode === 'wall' ? 'Roll' : 'Tilt'}</div>
                    <div
                        className={`font-mono font-bold mt-2 leading-none tabular-nums ${isLevel && granted ? 'text-[#00D37A]' : 'text-white'}`}
                        style={{ fontSize: 'clamp(56px, 14vw, 96px)' }}
                        data-testid="level-big-angle"
                    >
                        {bigAngle.toFixed(1)}°
                    </div>
                    {isLevel && granted && (
                        <div className="mt-3 font-mono text-xs uppercase tracking-[0.3em] text-[#00D37A]" data-testid="level-indicator">
                            Level
                        </div>
                    )}
                </div>
            </div>

            {mode === 'surface' ? (
                <div className={`grid-border border-white/15 aspect-square relative overflow-hidden ${isLevel && granted ? 'bg-[#00875A]/20' : 'bg-[#0A0A0C]'} transition-colors duration-200`} data-testid="spirit-level-canvas">
                    {/* Crosshair */}
                    <div className="absolute inset-0"><div className="absolute left-0 right-0 top-1/2 h-[1px] bg-white/15" /></div>
                    <div className="absolute inset-0"><div className="absolute top-0 bottom-0 left-1/2 w-[1px] bg-white/15" /></div>
                    {/* Concentric rings with degree labels */}
                    {[{ r: 15, label: '1°' }, { r: 35, label: '5°' }, { r: 55, label: '10°' }, { r: 78, label: '20°' }].map(({ r, label }) => (
                        <div key={label} className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="rounded-full border border-white/15" style={{ width: `${r}%`, height: `${r}%` }}>
                                <span className="absolute top-1/2 -translate-y-1/2 font-mono text-[9px] text-white/40" style={{ left: `calc(50% + ${r / 2}% + 4px)` }}>{label}</span>
                            </div>
                        </div>
                    ))}
                    {/* Center "target" */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-3 h-3 border border-white/30" />
                    </div>
                    {/* Bubble */}
                    <div
                        className="absolute left-1/2 top-1/2 w-20 h-20 rounded-full transition-[transform,background-color,box-shadow] duration-150 ease-out"
                        style={{
                            transform: `translate(-50%, -50%) translate(${(clampedX / 30) * 90}%, ${(clampedY / 30) * 90}%)`,
                            background: isLevel && granted ? 'rgba(0, 211, 122, 0.85)' : 'rgba(31, 165, 207, 0.85)',
                            boxShadow: isLevel && granted
                                ? '0 0 0 8px rgba(0, 211, 122, 0.25), 0 0 30px rgba(0, 211, 122, 0.6)'
                                : '0 0 0 6px rgba(31, 165, 207, 0.2)',
                        }}
                    />
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Horizontal vial (roll) */}
                    <Vial orientation="horizontal" value={gs} label="Roll (left / right)" testid="vial-horizontal" level={vialLevel(gs) && granted} />
                    {/* Vertical vial (pitch) */}
                    <Vial orientation="vertical" value={bs} label="Pitch (forward / back)" testid="vial-vertical" level={vialLevel(bs) && granted} />
                </div>
            )}

            {/* Raw axis readout */}
            <div className="grid grid-cols-2 mt-4 border border-white/15">
                <div className="p-3 border-r border-white/15">
                    <div className="label-tiny text-white/60">X · Roll</div>
                    <div className="font-mono text-xl font-bold mt-1 tabular-nums" data-testid="level-x">{gs.toFixed(1)}°</div>
                </div>
                <div className="p-3">
                    <div className="label-tiny text-white/60">Y · Pitch</div>
                    <div className="font-mono text-xl font-bold mt-1 tabular-nums" data-testid="level-y">{bs.toFixed(1)}°</div>
                </div>
            </div>

            {/* Calibration */}
            {granted && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                        onClick={zeroNow}
                        data-testid="calibrate-btn"
                        className="py-3 font-mono text-[11px] uppercase tracking-[0.2em] border border-white/25 text-white hover:bg-white/5 active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                    >
                        <Crosshair size={14} weight="bold" /> Calibrate · zero
                    </button>
                    <button
                        onClick={resetCalib}
                        data-testid="reset-calib-btn"
                        disabled={calib.beta === 0 && calib.gamma === 0}
                        className={`py-3 font-mono text-[11px] uppercase tracking-[0.2em] border border-white/25 inline-flex items-center justify-center gap-2 active:scale-95 transition-all duration-150 ${(calib.beta === 0 && calib.gamma === 0) ? 'text-white/30 cursor-not-allowed' : 'text-white hover:bg-white/5'}`}
                    >
                        <ArrowCounterClockwise size={14} weight="bold" /> Reset
                    </button>
                </div>
            )}

            {!granted && !unsupported && (
                <button
                    onClick={requestPermission}
                    data-testid="enable-level-btn"
                    className="w-full mt-5 bg-[#1FA5CF] text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                >
                    Enable motion sensors
                </button>
            )}
            {unsupported && (
                <div className="mt-5 border border-white/15 p-4 text-sm text-white/70 inline-flex items-start gap-2">
                    <Info size={18} className="flex-none mt-0.5" />
                    <span>Motion sensors aren't available on this device. Open DefectR on your phone to use the spirit level.</span>
                </div>
            )}
        </div>
    );
}

function Vial({ orientation, value, label, testid, level }) {
    const clamped = Math.max(-20, Math.min(20, value));
    const pct = ((clamped + 20) / 40) * 100; // 0..100
    const isH = orientation === 'horizontal';
    return (
        <div data-testid={testid}>
            <div className="label-tiny text-white/60 mb-2">{label}</div>
            <div
                className={`relative border ${level ? 'border-[#00D37A]' : 'border-white/20'} ${level ? 'bg-[#00875A]/20' : 'bg-[#0A0A0C]'} ${isH ? 'h-20' : 'h-48 w-24 mx-auto'} transition-colors duration-200 overflow-hidden`}
            >
                {/* Centre markers */}
                {isH ? (
                    <>
                        <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-white/40 -translate-x-1/2" />
                        <div className="absolute left-1/2 top-0 bottom-0 w-16 -translate-x-1/2 border-l border-r border-white/20" />
                        <div className="absolute top-0 bottom-0 left-[10%] w-[1px] bg-white/15" />
                        <div className="absolute top-0 bottom-0 left-[30%] w-[1px] bg-white/15" />
                        <div className="absolute top-0 bottom-0 right-[30%] w-[1px] bg-white/15" />
                        <div className="absolute top-0 bottom-0 right-[10%] w-[1px] bg-white/15" />
                    </>
                ) : (
                    <>
                        <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-white/40 -translate-y-1/2" />
                        <div className="absolute top-1/2 left-0 right-0 h-16 -translate-y-1/2 border-t border-b border-white/20" />
                        <div className="absolute left-0 right-0 top-[10%] h-[1px] bg-white/15" />
                        <div className="absolute left-0 right-0 top-[30%] h-[1px] bg-white/15" />
                        <div className="absolute left-0 right-0 bottom-[30%] h-[1px] bg-white/15" />
                        <div className="absolute left-0 right-0 bottom-[10%] h-[1px] bg-white/15" />
                    </>
                )}
                {/* Bubble */}
                <div
                    className="absolute rounded-full transition-all duration-100"
                    style={isH
                        ? {
                            width: '52px', height: '52px',
                            left: `${pct}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: level ? 'rgba(0, 211, 122, 0.9)' : 'rgba(31, 165, 207, 0.9)',
                            boxShadow: level ? '0 0 0 6px rgba(0, 211, 122, 0.25)' : '0 0 0 4px rgba(31, 165, 207, 0.2)',
                        }
                        : {
                            width: '52px', height: '52px',
                            top: `${100 - pct}%`,
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: level ? 'rgba(0, 211, 122, 0.9)' : 'rgba(31, 165, 207, 0.9)',
                            boxShadow: level ? '0 0 0 6px rgba(0, 211, 122, 0.25)' : '0 0 0 4px rgba(31, 165, 207, 0.2)',
                        }
                    }
                />
                {/* Reading chip */}
                <div className={`absolute ${isH ? 'left-2 top-1' : 'top-2 left-1/2 -translate-x-1/2'} font-mono text-[10px] uppercase tracking-[0.18em] ${level ? 'text-[#00D37A]' : 'text-white/60'}`}>
                    {value.toFixed(1)}°
                </div>
            </div>
        </div>
    );
}

function MeasureTool() {
    const [photo, setPhoto] = useState(null);             // dataURL
    const [stage, setStage] = useState('snap');           // 'snap' | 'calibrate' | 'measure'
    const [calibPts, setCalibPts] = useState([]);         // [{x%, y%}] up to 2
    const [refMm, setRefMm] = useState(23.43);            // known real-world length (mm) — default £1 coin
    const [refLabel, setRefLabel] = useState('£1 coin (23.43 mm)');
    const [scale, setScale] = useState(null);             // mm per normalized-% unit (along aspect)
    const [aspect, setAspect] = useState(1);              // image width/height ratio (to correct % -> px)
    const [measurements, setMeasurements] = useState([]); // [{id, a:{x,y}, b:{x,y}, mm}]
    const [pendingPts, setPendingPts] = useState([]);     // current measurement: 0, 1, or 2 pts
    const [unit, setUnit] = useState('cm');
    const fileRef = useRef(null);
    const imgRef = useRef(null);

    const presets = [
        { mm: 23.43, label: '£1 coin (23.43 mm)' },
        { mm: 24.25, label: '€1 coin (24.25 mm)' },
        { mm: 297, label: 'A4 long edge (297 mm)' },
        { mm: 210, label: 'A4 short edge (210 mm)' },
        { mm: 85.6, label: 'Bank / loyalty card (85.6 mm)' },
    ];

    const reset = (keepPhoto = false) => {
        if (!keepPhoto) setPhoto(null);
        setCalibPts([]); setScale(null); setMeasurements([]); setPendingPts([]);
        setStage(keepPhoto ? 'calibrate' : 'snap');
    };

    const onSnap = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            setPhoto(reader.result);
            setCalibPts([]); setScale(null); setMeasurements([]); setPendingPts([]);
            setStage('calibrate');
        };
        reader.readAsDataURL(f);
    };

    const onImgLoad = () => {
        if (imgRef.current) {
            const { naturalWidth: w, naturalHeight: h } = imgRef.current;
            if (w && h) setAspect(w / h);
        }
    };

    const handleTap = (e) => {
        if (!photo) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        const pt = { x, y };

        if (stage === 'calibrate') {
            if (calibPts.length < 2) {
                const next = [...calibPts, pt];
                setCalibPts(next);
                if (next.length === 2) {
                    // Compute scale: mm per %unit, where %unit is across the longer side
                    const distPct = pctDistance(next[0], next[1], aspect);
                    if (distPct > 0.01) setScale(refMm / distPct);
                }
            }
        } else if (stage === 'measure') {
            if (pendingPts.length === 0) {
                setPendingPts([pt]);
            } else if (pendingPts.length === 1) {
                const a = pendingPts[0];
                const b = pt;
                const distPct = pctDistance(a, b, aspect);
                const mm = scale ? scale * distPct : 0;
                setMeasurements(prev => [...prev, { id: crypto.randomUUID(), a, b, mm }]);
                setPendingPts([]);
            }
        }
    };

    const undoCalibPt = () => setCalibPts(prev => prev.slice(0, -1));
    const confirmCalibration = () => {
        if (scale) setStage('measure');
    };
    const redoCalibration = () => { setCalibPts([]); setScale(null); setStage('calibrate'); };

    const fmt = (mm) => {
        if (!mm) return '—';
        if (unit === 'in') return `${(mm / 25.4).toFixed(2)}″`;
        if (mm < 10) return `${mm.toFixed(1)} mm`;
        return `${(mm / 10).toFixed(1)} cm`;
    };

    const allPoints = stage === 'calibrate' ? calibPts : pendingPts;
    const instruction = getMeasureInstruction({ photo, stage, calibPts, pendingPts, refLabel });

    return (
        <div>
            <div className="label-tiny text-white/60 mb-3">Snap & Measure</div>

            {/* Photo canvas with overlay */}
            {photo ? (
                <div
                    onClick={handleTap}
                    className="relative border border-white/15 bg-[#0A0A0C] overflow-hidden select-none cursor-crosshair"
                    style={{ touchAction: 'manipulation' }}
                    data-testid="measure-canvas"
                >
                    <img
                        ref={imgRef}
                        src={photo}
                        alt=""
                        onLoad={onImgLoad}
                        className="w-full block pointer-events-none"
                        draggable={false}
                    />
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {/* Calibration line */}
                        {calibPts.length === 2 && (
                            <line x1={calibPts[0].x} y1={calibPts[0].y} x2={calibPts[1].x} y2={calibPts[1].y} stroke="#1FA5CF" strokeWidth="0.4" strokeDasharray="1 1" vectorEffect="non-scaling-stroke" />
                        )}
                        {/* Pending measurement line */}
                        {pendingPts.length === 2 && (
                            <line x1={pendingPts[0].x} y1={pendingPts[0].y} x2={pendingPts[1].x} y2={pendingPts[1].y} stroke="#00D37A" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
                        )}
                        {/* Completed measurement lines */}
                        {measurements.map((m) => (
                            <line key={m.id} x1={m.a.x} y1={m.a.y} x2={m.b.x} y2={m.b.y} stroke="#00D37A" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
                        ))}
                    </svg>

                    {/* Points */}
                    {allPoints.map((p, i) => (
                        <MeasurePoint key={`p-${i}`} point={p} color={stage === 'calibrate' ? '#1FA5CF' : '#00D37A'} label={i + 1} />
                    ))}
                    {measurements.map((m) => (
                        <React.Fragment key={m.id}>
                            <MeasurePoint point={m.a} color="#00D37A" />
                            <MeasurePoint point={m.b} color="#00D37A" />
                            {/* Label at midpoint */}
                            <div
                                className="absolute -translate-x-1/2 -translate-y-1/2 bg-[#00D37A] text-[#09090B] font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 whitespace-nowrap pointer-events-none"
                                style={{ left: `${(m.a.x + m.b.x) / 2}%`, top: `${(m.a.y + m.b.y) / 2}%` }}
                            >{fmt(m.mm)}</div>
                        </React.Fragment>
                    ))}
                </div>
            ) : (
                <div className="border border-dashed border-white/25 p-10 text-center" data-testid="measure-empty">
                    <Camera size={32} color="#1FA5CF" weight="duotone" className="mx-auto" />
                    <p className="mt-3 font-display text-lg text-white">Snap a photo to measure</p>
                    <p className="mt-1 text-xs text-white/60">Include a coin, sheet of paper or any card for scale.</p>
                    <p className="mt-2 text-[10px] text-white/40 font-mono uppercase tracking-[0.18em]" data-testid="measure-privacy-note">
                        Photos stay on your device · nothing uploaded · no card data read
                    </p>
                </div>
            )}

            {/* Instruction strip */}
            <div className="mt-3 border border-white/15 bg-[#0A0A0C] p-3 text-sm text-white/80 inline-flex items-start gap-2 w-full" data-testid="measure-instruction">
                <Info size={16} weight="bold" color="#1FA5CF" className="mt-0.5 flex-none" />
                <span>{instruction}</span>
            </div>

            {/* Reference selector (shown during calibrate or before first photo) */}
            {(stage === 'calibrate' || !photo) && (
                <div className="mt-3 border border-white/15 p-3">
                    <div className="label-tiny text-white/60 mb-2">Reference object</div>
                    <select
                        data-testid="measure-ref-select"
                        value={refMm}
                        onChange={(e) => {
                            const mm = parseFloat(e.target.value);
                            const preset = presets.find(p => p.mm === mm);
                            setRefMm(mm); setRefLabel(preset?.label || `Custom (${mm} mm)`);
                            // If already calibrated with 2 pts, rescale immediately
                            if (calibPts.length === 2) {
                                const distPct = pctDistance(calibPts[0], calibPts[1], aspect);
                                if (distPct > 0.01) setScale(mm / distPct);
                            }
                        }}
                        className="w-full bg-transparent border border-white/25 px-3 py-2 text-white"
                    >
                        {presets.map(p => <option key={p.label} value={p.mm} className="bg-[#09090B]">{p.label}</option>)}
                    </select>
                    <div className="mt-2 flex items-center gap-2">
                        <span className="label-tiny text-white/60">Custom (mm)</span>
                        <input
                            type="number"
                            step="0.1"
                            min="1"
                            value={refMm}
                            data-testid="measure-ref-custom"
                            onChange={(e) => {
                                const mm = parseFloat(e.target.value) || 0;
                                setRefMm(mm); setRefLabel(`Custom (${mm} mm)`);
                                if (calibPts.length === 2 && mm > 0) {
                                    const distPct = pctDistance(calibPts[0], calibPts[1], aspect);
                                    if (distPct > 0.01) setScale(mm / distPct);
                                }
                            }}
                            className="flex-1 bg-transparent border border-white/25 px-3 py-2 text-white outline-none focus:border-[#1FA5CF]"
                        />
                    </div>
                </div>
            )}

            {/* Action buttons */}
            <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                    onClick={() => fileRef.current?.click()}
                    data-testid="snap-photo-btn"
                    className="bg-[#1FA5CF] text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 inline-flex items-center justify-center gap-2"
                >
                    <Camera size={14} weight="bold" /> {photo ? 'Retake photo' : 'Take photo'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onSnap} className="hidden" data-testid="snap-file-input" />

                {stage === 'calibrate' && calibPts.length > 0 && (
                    <button
                        onClick={calibPts.length === 2 ? confirmCalibration : undoCalibPt}
                        data-testid={calibPts.length === 2 ? 'confirm-calib-btn' : 'undo-calib-pt-btn'}
                        disabled={calibPts.length === 2 && !scale}
                        className="border border-white/25 text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white/5 active:scale-95 transition-all duration-150"
                    >
                        {calibPts.length === 2 ? 'Start measuring →' : 'Undo point'}
                    </button>
                )}
                {stage === 'measure' && (
                    <button
                        onClick={redoCalibration}
                        data-testid="redo-calib-btn"
                        className="border border-white/25 text-white py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-white/5 active:scale-95 transition-all duration-150"
                    >
                        Redo calibration
                    </button>
                )}
            </div>

            {/* Scale readout + unit */}
            {scale && (
                <div className="mt-4 grid grid-cols-2 border border-white/15">
                    <div className="p-3 border-r border-white/15">
                        <div className="label-tiny text-white/60">Scale</div>
                        <div className="font-mono text-sm text-white mt-1">1% ≈ {scale.toFixed(2)} mm</div>
                    </div>
                    <div className="p-0 flex">
                        {['cm', 'in'].map(u => (
                            <button
                                key={u}
                                onClick={() => setUnit(u)}
                                data-testid={`unit-${u}`}
                                className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.2em] ${unit === u ? 'bg-[#1FA5CF] text-white' : 'text-white/70'}`}
                            >{u}</button>
                        ))}
                    </div>
                </div>
            )}

            {/* Measurements list */}
            {measurements.length > 0 && (
                <div className="mt-4 border border-white/15">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-white/15">
                        <div className="label-tiny text-white/60">Measurements ({measurements.length})</div>
                        <button onClick={() => setMeasurements([])} data-testid="clear-measures-btn" className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/60 hover:text-[#1FA5CF]">Clear all</button>
                    </div>
                    <ul className="divide-y divide-white/10">
                        {measurements.map((m, i) => (
                            <li key={m.id} className="p-3 flex items-center justify-between text-sm" data-testid={`measure-row-${m.id}`}>
                                <span className="font-mono text-white">
                                    <span className="inline-block w-5 h-5 bg-[#00D37A] text-[#09090B] text-[10px] font-bold mr-2 text-center leading-5">{i + 1}</span>
                                    {fmt(m.mm)}
                                </span>
                                <button
                                    onClick={() => setMeasurements(prev => prev.filter(x => x.id !== m.id))}
                                    className="text-white/50 hover:text-[#E53935] text-xs font-mono uppercase tracking-[0.18em]"
                                >Remove</button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {photo && (
                <button
                    onClick={() => reset(false)}
                    data-testid="reset-all-btn"
                    className="mt-3 w-full py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/60 hover:text-white"
                >
                    Start over (new photo)
                </button>
            )}
        </div>
    );
}

function MeasurePoint({ point, color, label }) {
    return (
        <div
            className="absolute pointer-events-none -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
        >
            <div
                className="w-4 h-4 rounded-full border-2 border-white"
                style={{ background: color, boxShadow: `0 0 0 3px ${color}33` }}
            />
            {label != null && (
                <div className="absolute -top-1 left-4 text-white text-[10px] font-mono font-bold bg-[#09090B] px-1">{label}</div>
            )}
        </div>
    );
}

// Distance in percent-space, correcting for non-square aspect.
// Because our SVG viewBox is 0-100 on both axes (non-preserved), we must
// convert y% back into the same units as x% using the image aspect ratio.
function pctDistance(a, b, aspect) {
    const dx = (b.x - a.x);
    const dy = (b.y - a.y) / aspect; // treat 1%-of-width as the unit
    return Math.sqrt(dx * dx + dy * dy);
}

// Human-readable instruction for the Snap & Measure flow state machine.
function getMeasureInstruction({ photo, stage, calibPts, pendingPts, refLabel }) {
    if (!photo) {
        return 'Snap a photo containing a known-size object (e.g. a £1 coin or A4 sheet) in the same plane as what you want to measure.';
    }
    if (stage === 'calibrate') {
        if (calibPts.length === 0) {
            const refShort = refLabel.split(' (')[0].toLowerCase();
            return `Tap one end of your ${refShort}.`;
        }
        if (calibPts.length === 1) return 'Tap the other end.';
        return 'Scale set. Tap "Start measuring" — or redo if needed.';
    }
    // stage === 'measure'
    return pendingPts.length === 0 ? 'Tap the first point you want to measure.' : 'Tap the second point.';
}
